/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { IPersonaProps } from '@fluentui/react/lib/Persona';
import { IBasePicker } from '@fluentui/react/lib/Pickers';
import * as React from 'react';
import { ApprovalPersonaProps } from '../../../models/ApprovalPersonaProps';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { stringFormat } from '../../../utilities/Utils';

export interface Recipient {
    name: string;
    order: string;
    role: number;
    persona: ApprovalPersonaProps;
    userAdded: boolean;
    emailRef: React.RefObject<IBasePicker<IPersonaProps>>;
}

/** *
 * Create a deep copy of recipient, with additonal parameters.
 *
 * @param recipient the recipient to be copied.
 * @param order Order value of the recipient, should be empty string or
 * a string representing a positive number up to 2 digits.
 * @param userAdded Boolean value if the recipient is added by user.
 */
export const copy = (recipient: Recipient, order?: string, userAdded?: boolean): Recipient => {
    const result = { ...recipient };

    if (recipient.emailRef) {
        result.emailRef = { ...recipient.emailRef };
    }

    if (recipient.persona) {
        result.persona = recipient.persona;
    }

    if (order !== undefined) {
        result.order = order;
    }

    if (userAdded !== undefined) {
        result.userAdded = userAdded;
    }

    return result;
};

export enum RecipientTypeLabel {
    Role,
    Action,
}

export const getRecipientTypeAriaLabel = (recipientTypeLabel: RecipientTypeLabel, recipientType: string): string => {
    const loc = getAppHost().getLocale();

    if (recipientTypeLabel === RecipientTypeLabel.Role) {
        return stringFormat(loc.strings.recipient_list_role_aria_label_format, {
            role: recipientType,
        });
    } else if (recipientTypeLabel === RecipientTypeLabel.Action)
        return stringFormat(loc.strings.recipient_list_action_aria_label_format, {
            action: recipientType,
        });
    return null;
};

export const getRecipientTypeLabel = (recipientTypeLabel: RecipientTypeLabel): string => {
    const loc = getAppHost().getLocale();

    const roleLabelMap = {
        [RecipientTypeLabel.Role]: loc.strings.recipient_list_column_name_role,
        [RecipientTypeLabel.Action]: loc.strings.recipient_list_column_name_action,
    };

    return roleLabelMap[recipientTypeLabel];
};

export const emptyRecipient: Recipient = {
    name: null,
    order: null,
    role: 0,
    persona: null,
    userAdded: false,
    emailRef: React.createRef<IBasePicker<IPersonaProps>>(),
};
